import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Play, Pause, Copy, Trash2, Edit, Phone, Mail, MessageSquare, Clock,
  Users, ChevronRight, ArrowRight, CheckCircle2, XCircle, AlertCircle,
  GripVertical, Settings, Zap, MoreVertical, BarChart3, TrendingUp, Eye
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL;

// Step type configurations
const STEP_TYPES = {
  ai_call: {
    label: 'AI Call',
    icon: Phone,
    color: 'bg-blue-500',
    description: 'Make an AI-powered voice call'
  },
  email: {
    label: 'Email',
    icon: Mail,
    color: 'bg-purple-500',
    description: 'Send a personalized email'
  },
  sms: {
    label: 'SMS',
    icon: MessageSquare,
    color: 'bg-green-500',
    description: 'Send a text message'
  },
  wait: {
    label: 'Wait',
    icon: Clock,
    color: 'bg-orange-500',
    description: 'Wait before next step'
  }
};

// Outcome options for branching
const OUTCOME_OPTIONS = [
  { value: 'any', label: 'Any outcome' },
  { value: 'answered', label: 'Call answered' },
  { value: 'meeting_booked', label: 'Meeting booked' },
  { value: 'interested', label: 'Interested' },
  { value: 'callback_requested', label: 'Callback requested' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'voicemail_dropped', label: 'Voicemail dropped' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'sent', label: 'Email/SMS sent' },
  { value: 'failed', label: 'Failed' }
];

// Step icon helper - moved outside component for performance
const StepIcon = ({ type, className = '' }) => {
  const config = STEP_TYPES[type];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <div className={`p-2 rounded-lg ${config.color} text-white ${className}`}>
      <Icon className="h-4 w-4" />
    </div>
  );
};

export default function SequencesPage() {
  // State
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [builderDialogOpen, setBuilderDialogOpen] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  
  // Selected items
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [sequenceToDelete, setSequenceToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    allow_re_enrollment: false
  });
  
  // Step form state
  const [stepFormData, setStepFormData] = useState({
    type: 'ai_call',
    name: '',
    config: {},
    triggers: []
  });
  
  // Enrollment state
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  
  // Fetch data
  const fetchSequences = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/sequences`);
      setSequences(response.data || []);
    } catch (error) {
      console.error('Error fetching sequences:', error);
      toast.error('Failed to load sequences');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/api/ai-calls/campaigns`);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };
  
  const fetchTemplates = async () => {
    try {
      const [emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/api/templates?type=email`),
        axios.get(`${API}/api/templates?type=sms`)
      ]);
      setEmailTemplates(emailRes.data || []);
      setSmsTemplates(smsRes.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };
  
  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/api/leads?limit=500`);
      setLeads(response.data || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };
  
  useEffect(() => {
    fetchSequences();
    fetchCampaigns();
    fetchTemplates();
  }, [fetchSequences]);
  
  // Handlers
  const handleCreateSequence = async () => {
    try {
      const response = await axios.post(`${API}/api/sequences`, formData);
      toast.success('Sequence created');
      setSequences([response.data, ...sequences]);
      setCreateDialogOpen(false);
      setFormData({ name: '', description: '', allow_re_enrollment: false });
      
      // Open builder for new sequence
      setSelectedSequence(response.data);
      setBuilderDialogOpen(true);
    } catch (error) {
      console.error('Error creating sequence:', error);
      toast.error('Failed to create sequence');
    }
  };
  
  const handleUpdateSequence = async (sequenceId, updates) => {
    try {
      const response = await axios.put(`${API}/api/sequences/${sequenceId}`, updates);
      setSequences(sequences.map(s => s.id === sequenceId ? response.data : s));
      if (selectedSequence?.id === sequenceId) {
        setSelectedSequence(response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Error updating sequence:', error);
      toast.error('Failed to update sequence');
      throw error;
    }
  };
  
  const handleDeleteSequence = async () => {
    if (!sequenceToDelete) return;
    
    try {
      await axios.delete(`${API}/api/sequences/${sequenceToDelete.id}`);
      toast.success('Sequence deleted');
      setSequences(sequences.filter(s => s.id !== sequenceToDelete.id));
      setDeleteDialogOpen(false);
      setSequenceToDelete(null);
    } catch (error) {
      console.error('Error deleting sequence:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete sequence');
    }
  };
  
  const handleDuplicateSequence = async (sequence) => {
    try {
      const response = await axios.post(`${API}/api/sequences/${sequence.id}/duplicate`);
      toast.success('Sequence duplicated');
      setSequences([response.data, ...sequences]);
    } catch (error) {
      console.error('Error duplicating sequence:', error);
      toast.error('Failed to duplicate sequence');
    }
  };
  
  const handleActivateSequence = async (sequence) => {
    try {
      await axios.post(`${API}/api/sequences/${sequence.id}/activate`);
      toast.success('Sequence activated');
      fetchSequences();
    } catch (error) {
      console.error('Error activating sequence:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate sequence');
    }
  };
  
  const handlePauseSequence = async (sequence) => {
    try {
      await axios.post(`${API}/api/sequences/${sequence.id}/pause`);
      toast.success('Sequence paused');
      fetchSequences();
    } catch (error) {
      console.error('Error pausing sequence:', error);
      toast.error('Failed to pause sequence');
    }
  };
  
  const handleResumeSequence = async (sequence) => {
    try {
      await axios.post(`${API}/api/sequences/${sequence.id}/resume`);
      toast.success('Sequence resumed');
      fetchSequences();
    } catch (error) {
      console.error('Error resuming sequence:', error);
      toast.error('Failed to resume sequence');
    }
  };
  
  // Step handlers
  const handleAddStep = async () => {
    if (!selectedSequence) return;
    
    try {
      // Determine trigger based on existing steps
      const steps = selectedSequence.steps || [];
      const triggers = steps.length > 0 ? [{
        from_step_id: steps[steps.length - 1].id,
        on_outcomes: ['any']
      }] : [];
      
      const response = await axios.post(`${API}/api/sequences/${selectedSequence.id}/steps`, {
        ...stepFormData,
        triggers
      });
      
      // Update sequence with new step
      const updatedSteps = [...steps, response.data];
      const updated = await handleUpdateSequence(selectedSequence.id, {
        steps: updatedSteps,
        entry_step_id: selectedSequence.entry_step_id || response.data.id
      });
      
      setSelectedSequence(updated);
      setStepDialogOpen(false);
      setStepFormData({ type: 'ai_call', name: '', config: {}, triggers: [] });
      toast.success('Step added');
    } catch (error) {
      console.error('Error adding step:', error);
      toast.error('Failed to add step');
    }
  };
  
  const handleUpdateStep = async () => {
    if (!selectedSequence || !selectedStep) return;
    
    try {
      await axios.put(`${API}/api/sequences/${selectedSequence.id}/steps/${selectedStep.id}`, stepFormData);
      
      // Refresh sequence
      const response = await axios.get(`${API}/api/sequences/${selectedSequence.id}`);
      setSelectedSequence(response.data);
      setStepDialogOpen(false);
      setSelectedStep(null);
      toast.success('Step updated');
    } catch (error) {
      console.error('Error updating step:', error);
      toast.error('Failed to update step');
    }
  };
  
  const handleDeleteStep = async (stepId) => {
    if (!selectedSequence) return;
    
    try {
      await axios.delete(`${API}/api/sequences/${selectedSequence.id}/steps/${stepId}`);
      
      // Refresh sequence
      const response = await axios.get(`${API}/api/sequences/${selectedSequence.id}`);
      setSelectedSequence(response.data);
      toast.success('Step deleted');
    } catch (error) {
      console.error('Error deleting step:', error);
      toast.error('Failed to delete step');
    }
  };
  
  // Enrollment handlers
  const handleEnrollLeads = async () => {
    if (!selectedSequence || selectedLeadIds.length === 0) return;
    
    try {
      const response = await axios.post(`${API}/api/sequences/${selectedSequence.id}/enroll`, {
        lead_ids: selectedLeadIds
      });
      
      const result = response.data;
      if (result.enrolled > 0) {
        toast.success(`${result.enrolled} leads enrolled`);
      }
      if (result.skipped > 0) {
        toast.warning(`${result.skipped} leads skipped`);
      }
      
      setEnrollDialogOpen(false);
      setSelectedLeadIds([]);
      fetchSequences();
    } catch (error) {
      console.error('Error enrolling leads:', error);
      toast.error(error.response?.data?.detail || 'Failed to enroll leads');
    }
  };
  
  const openEnrollDialog = (sequence) => {
    setSelectedSequence(sequence);
    fetchLeads();
    setSelectedLeadIds([]);
    setLeadSearchQuery('');
    setEnrollDialogOpen(true);
  };
  
  const openBuilder = (sequence) => {
    setSelectedSequence(sequence);
    setBuilderDialogOpen(true);
  };
  
  const openStepDialog = (step = null) => {
    if (step) {
      setSelectedStep(step);
      setStepFormData({
        type: step.type,
        name: step.name,
        config: step.config || {},
        triggers: step.triggers || []
      });
    } else {
      setSelectedStep(null);
      setStepFormData({ type: 'ai_call', name: '', config: {}, triggers: [] });
    }
    setStepDialogOpen(true);
  };
  
  // Filter leads by search
  const filteredLeads = leads.filter(lead => {
    if (!leadSearchQuery) return true;
    const query = leadSearchQuery.toLowerCase();
    return (
      lead.first_name?.toLowerCase().includes(query) ||
      lead.last_name?.toLowerCase().includes(query) ||
      lead.company_name?.toLowerCase().includes(query) ||
      lead.work_email?.toLowerCase().includes(query)
    );
  });
  
  // Status badge helper
  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'secondary', label: 'Draft' },
      active: { variant: 'default', label: 'Active', className: 'bg-green-500' },
      paused: { variant: 'warning', label: 'Paused', className: 'bg-yellow-500' },
      archived: { variant: 'outline', label: 'Archived' }
    };
    const c = config[status] || config.draft;
    return <Badge variant={c.variant} className={c.className}>{c.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="sequences-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Multi-Channel Sequences</h1>
          <p className="text-slate-500">Automate outreach with AI calls, emails, and SMS</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="new-sequence-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Sequence
        </Button>
      </div>
      
      {/* Sequences Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : sequences.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Zap className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">No sequences yet</h3>
            <p className="text-slate-500 mb-4">Create your first multi-channel sequence to automate outreach</p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Sequence
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sequences.map((sequence) => (
            <Card key={sequence.id} className="hover:shadow-md transition-shadow" data-testid={`sequence-card-${sequence.id}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{sequence.name}</CardTitle>
                    <CardDescription className="line-clamp-2">{sequence.description || 'No description'}</CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openBuilder(sequence)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Steps
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicateSequence(sequence)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {sequence.status === 'active' ? (
                        <DropdownMenuItem onClick={() => handlePauseSequence(sequence)}>
                          <Pause className="h-4 w-4 mr-2" />
                          Pause
                        </DropdownMenuItem>
                      ) : sequence.status === 'paused' ? (
                        <DropdownMenuItem onClick={() => handleResumeSequence(sequence)}>
                          <Play className="h-4 w-4 mr-2" />
                          Resume
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => handleActivateSequence(sequence)}>
                          <Play className="h-4 w-4 mr-2" />
                          Activate
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => { setSequenceToDelete(sequence); setDeleteDialogOpen(true); }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {/* Status and Stats */}
                <div className="flex items-center gap-2 mb-3">
                  {getStatusBadge(sequence.status)}
                  <Badge variant="outline">
                    {(sequence.steps || []).length} steps
                  </Badge>
                </div>
                
                {/* Steps Preview */}
                <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-2">
                  {(sequence.steps || []).slice(0, 5).map((step, idx) => (
                    <React.Fragment key={step.id}>
                      <StepIcon type={step.type} />
                      {idx < Math.min((sequence.steps || []).length - 1, 4) && (
                        <ArrowRight className="h-3 w-3 text-slate-300 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
                  {(sequence.steps || []).length > 5 && (
                    <span className="text-xs text-slate-500">+{(sequence.steps || []).length - 5}</span>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div>
                    <div className="font-semibold">{sequence.total_enrolled || 0}</div>
                    <div className="text-slate-500 text-xs">Enrolled</div>
                  </div>
                  <div>
                    <div className="font-semibold text-green-600">{sequence.completed || 0}</div>
                    <div className="text-slate-500 text-xs">Completed</div>
                  </div>
                  <div>
                    <div className="font-semibold text-blue-600">{sequence.converted || 0}</div>
                    <div className="text-slate-500 text-xs">Converted</div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openBuilder(sequence)}>
                    <Settings className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {sequence.status === 'active' && (
                    <Button size="sm" className="flex-1" onClick={() => openEnrollDialog(sequence)}>
                      <Users className="h-4 w-4 mr-1" />
                      Enroll
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Create Sequence Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Sequence</DialogTitle>
            <DialogDescription>Set up a multi-channel outreach workflow</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Sequence Name</Label>
              <Input
                placeholder="e.g., Sales Outreach Q1"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="sequence-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Describe the purpose of this sequence..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <div className="font-medium">Allow Re-enrollment</div>
                <div className="text-sm text-slate-500">Let leads be enrolled again after completion</div>
              </div>
              <Switch
                checked={formData.allow_re_enrollment}
                onCheckedChange={(checked) => setFormData({ ...formData, allow_re_enrollment: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateSequence} disabled={!formData.name} data-testid="create-sequence-btn">
              Create & Build
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Sequence Builder Dialog */}
      <Dialog open={builderDialogOpen} onOpenChange={setBuilderDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              {selectedSequence?.name}
              {selectedSequence && getStatusBadge(selectedSequence.status)}
            </DialogTitle>
            <DialogDescription>{selectedSequence?.description || 'Build your sequence workflow'}</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="steps" className="flex-1 overflow-hidden flex flex-col">
            <TabsList>
              <TabsTrigger value="steps">Steps</TabsTrigger>
              <TabsTrigger value="enrollments">Enrollments</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            
            {/* Steps Tab */}
            <TabsContent value="steps" className="flex-1 overflow-auto">
              <div className="p-4 space-y-4">
                {/* Steps Timeline */}
                {(selectedSequence?.steps || []).length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <Clock className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 mb-3">No steps yet. Add your first step to get started.</p>
                    <Button onClick={() => openStepDialog()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Step
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(selectedSequence?.steps || []).map((step, idx) => (
                      <div key={step.id} className="flex items-start gap-3">
                        {/* Step Number */}
                        <div className="flex flex-col items-center">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium">
                            {idx + 1}
                          </div>
                          {idx < (selectedSequence?.steps || []).length - 1 && (
                            <div className="w-0.5 h-8 bg-slate-200 my-1"></div>
                          )}
                        </div>
                        
                        {/* Step Card */}
                        <Card className="flex-1">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <StepIcon type={step.type} />
                                <div>
                                  <div className="font-medium">{step.name}</div>
                                  <div className="text-sm text-slate-500">{STEP_TYPES[step.type]?.description}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => openStepDialog(step)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteStep(step.id)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Step Config Preview */}
                            {step.type === 'wait' && step.config && (
                              <div className="mt-2 text-sm text-slate-600">
                                Wait {step.config.delay_days || 0} days {step.config.delay_hours || 0} hours
                              </div>
                            )}
                            {step.type === 'email' && step.config?.template_id && (
                              <div className="mt-2 text-sm text-slate-600">
                                Template: {emailTemplates.find(t => t.id === step.config.template_id)?.name || 'Custom'}
                              </div>
                            )}
                            
                            {/* Triggers */}
                            {step.triggers && step.triggers.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {step.triggers.map((trigger, tIdx) => (
                                  <Badge key={tIdx} variant="outline" className="text-xs">
                                    on: {trigger.on_outcomes?.join(', ') || 'any'}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                    
                    {/* Add Step Button */}
                    <div className="flex items-center gap-3 pl-11">
                      <Button variant="outline" onClick={() => openStepDialog()}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Step
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Enrollments Tab */}
            <TabsContent value="enrollments" className="flex-1 overflow-auto">
              <EnrollmentsTab sequence={selectedSequence} />
            </TabsContent>
            
            {/* Analytics Tab */}
            <TabsContent value="analytics" className="flex-1 overflow-auto">
              <AnalyticsTab sequence={selectedSequence} />
            </TabsContent>
            
            {/* Settings Tab */}
            <TabsContent value="settings" className="flex-1 overflow-auto p-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Sequence Name</Label>
                  <Input
                    value={selectedSequence?.name || ''}
                    onChange={(e) => handleUpdateSequence(selectedSequence?.id, { name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={selectedSequence?.description || ''}
                    onChange={(e) => handleUpdateSequence(selectedSequence?.id, { description: e.target.value })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">Allow Re-enrollment</div>
                    <div className="text-sm text-slate-500">Let leads be enrolled again after completion</div>
                  </div>
                  <Switch
                    checked={selectedSequence?.allow_re_enrollment || false}
                    onCheckedChange={(checked) => handleUpdateSequence(selectedSequence?.id, { allow_re_enrollment: checked })}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="border-t pt-4">
            {selectedSequence?.status === 'draft' && (selectedSequence?.steps || []).length > 0 && (
              <Button onClick={() => handleActivateSequence(selectedSequence)}>
                <Play className="h-4 w-4 mr-2" />
                Activate Sequence
              </Button>
            )}
            <Button variant="outline" onClick={() => setBuilderDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Step Dialog */}
      <Dialog open={stepDialogOpen} onOpenChange={setStepDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedStep ? 'Edit Step' : 'Add Step'}</DialogTitle>
            <DialogDescription>Configure the step action and triggers</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Step Type */}
            <div className="space-y-2">
              <Label>Step Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(STEP_TYPES).map(([type, config]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setStepFormData({ ...stepFormData, type, name: stepFormData.name || config.label })}
                    className={`p-3 border rounded-lg text-left hover:bg-slate-50 transition-colors ${
                      stepFormData.type === type ? 'border-blue-500 bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded ${config.color} text-white`}>
                        <config.icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-medium">{config.label}</div>
                        <div className="text-xs text-slate-500">{config.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Step Name */}
            <div className="space-y-2">
              <Label>Step Name</Label>
              <Input
                placeholder="e.g., Initial Call, Follow-up Email"
                value={stepFormData.name}
                onChange={(e) => setStepFormData({ ...stepFormData, name: e.target.value })}
              />
            </div>
            
            {/* Type-specific Config */}
            {stepFormData.type === 'wait' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Days</Label>
                  <Input
                    type="number"
                    min="0"
                    value={stepFormData.config.delay_days || 0}
                    onChange={(e) => setStepFormData({
                      ...stepFormData,
                      config: { ...stepFormData.config, delay_days: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hours</Label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={stepFormData.config.delay_hours || 0}
                    onChange={(e) => setStepFormData({
                      ...stepFormData,
                      config: { ...stepFormData.config, delay_hours: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>
            )}
            
            {stepFormData.type === 'email' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Email Template</Label>
                  <Select
                    value={stepFormData.config.template_id || 'custom'}
                    onValueChange={(value) => setStepFormData({
                      ...stepFormData,
                      config: { ...stepFormData.config, template_id: value === 'custom' ? null : value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom content</SelectItem>
                      {emailTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!stepFormData.config.template_id && (
                  <>
                    <div className="space-y-2">
                      <Label>Subject</Label>
                      <Input
                        placeholder="Email subject"
                        value={stepFormData.config.subject || ''}
                        onChange={(e) => setStepFormData({
                          ...stepFormData,
                          config: { ...stepFormData.config, subject: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Content</Label>
                      <Textarea
                        placeholder="Email content... Use {{first_name}}, {{company}} for personalization"
                        rows={4}
                        value={stepFormData.config.content || ''}
                        onChange={(e) => setStepFormData({
                          ...stepFormData,
                          config: { ...stepFormData.config, content: e.target.value }
                        })}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            
            {stepFormData.type === 'sms' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>SMS Template</Label>
                  <Select
                    value={stepFormData.config.template_id || 'custom'}
                    onValueChange={(value) => setStepFormData({
                      ...stepFormData,
                      config: { ...stepFormData.config, template_id: value === 'custom' ? null : value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom message</SelectItem>
                      {smsTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!stepFormData.config.template_id && (
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      placeholder="SMS message... Use {{first_name}} for personalization"
                      rows={3}
                      value={stepFormData.config.message || ''}
                      onChange={(e) => setStepFormData({
                        ...stepFormData,
                        config: { ...stepFormData.config, message: e.target.value }
                      })}
                    />
                    <div className="text-xs text-slate-500">{(stepFormData.config.message || '').length}/160 characters</div>
                  </div>
                )}
              </div>
            )}
            
            {stepFormData.type === 'ai_call' && (
              <div className="space-y-2">
                <Label>AI Call Campaign</Label>
                <Select
                  value={stepFormData.config.campaign_id || 'none'}
                  onValueChange={(value) => setStepFormData({
                    ...stepFormData,
                    config: { ...stepFormData.config, campaign_id: value === 'none' ? null : value }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Use sequence settings</SelectItem>
                    {campaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">The AI call will use the selected campaign&apos;s voice and script</p>
              </div>
            )}
            
            {/* Triggers (for non-first steps) */}
            {selectedStep && (selectedSequence?.steps || []).findIndex(s => s.id === selectedStep.id) > 0 && (
              <div className="space-y-2">
                <Label>Trigger on outcomes</Label>
                <Select
                  value={(stepFormData.triggers?.[0]?.on_outcomes || ['any'])[0]}
                  onValueChange={(value) => setStepFormData({
                    ...stepFormData,
                    triggers: [{
                      ...stepFormData.triggers?.[0],
                      on_outcomes: value === 'any' ? ['any'] : [value]
                    }]
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OUTCOME_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStepDialogOpen(false)}>Cancel</Button>
            <Button onClick={selectedStep ? handleUpdateStep : handleAddStep} disabled={!stepFormData.name}>
              {selectedStep ? 'Update Step' : 'Add Step'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Enroll Leads Dialog */}
      <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Enroll Leads in Sequence</DialogTitle>
            <DialogDescription>
              Select leads to enroll in &quot;{selectedSequence?.name}&quot;
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder="Search leads..."
              value={leadSearchQuery}
              onChange={(e) => setLeadSearchQuery(e.target.value)}
            />
            
            <div className="border rounded-lg max-h-64 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedLeadIds.length === filteredLeads.length && filteredLeads.length > 0}
                        onChange={(e) => setSelectedLeadIds(e.target.checked ? filteredLeads.map(l => l.id) : [])}
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.slice(0, 50).map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedLeadIds.includes(lead.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLeadIds([...selectedLeadIds, lead.id]);
                            } else {
                              setSelectedLeadIds(selectedLeadIds.filter(id => id !== lead.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{lead.first_name} {lead.last_name}</TableCell>
                      <TableCell>{lead.company_name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{lead.work_email}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <div className="text-sm text-slate-500">
              {selectedLeadIds.length} leads selected
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEnrollLeads} disabled={selectedLeadIds.length === 0}>
              Enroll {selectedLeadIds.length} Leads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Sequence</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{sequenceToDelete?.name}&quot;? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteSequence}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Enrollments Tab Component
function EnrollmentsTab({ sequence }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!sequence?.id) return;
    
    const fetchEnrollments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/sequences/${sequence.id}/enrollments`);
        setEnrollments(response.data || []);
      } catch (error) {
        console.error('Error fetching enrollments:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEnrollments();
  }, [sequence?.id]);
  
  const handlePauseEnrollment = async (enrollmentId) => {
    try {
      await axios.post(`${API}/api/sequences/${sequence.id}/enrollments/${enrollmentId}/pause`);
      toast.success('Enrollment paused');
      setEnrollments(enrollments.map(e => e.id === enrollmentId ? { ...e, status: 'paused' } : e));
    } catch (error) {
      toast.error('Failed to pause');
    }
  };
  
  const handleResumeEnrollment = async (enrollmentId) => {
    try {
      await axios.post(`${API}/api/sequences/${sequence.id}/enrollments/${enrollmentId}/resume`);
      toast.success('Enrollment resumed');
      setEnrollments(enrollments.map(e => e.id === enrollmentId ? { ...e, status: 'active' } : e));
    } catch (error) {
      toast.error('Failed to resume');
    }
  };
  
  const handleUnenroll = async (enrollmentId) => {
    try {
      await axios.delete(`${API}/api/sequences/${sequence.id}/enrollments/${enrollmentId}`);
      toast.success('Lead unenrolled');
      setEnrollments(enrollments.filter(e => e.id !== enrollmentId));
    } catch (error) {
      toast.error('Failed to unenroll');
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <Play className="h-4 w-4 text-green-500" />;
      case 'paused': return <Pause className="h-4 w-4 text-yellow-500" />;
      case 'completed': return <CheckCircle2 className="h-4 w-4 text-blue-500" />;
      case 'converted': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <AlertCircle className="h-4 w-4 text-slate-400" />;
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (enrollments.length === 0) {
    return (
      <div className="text-center py-8">
        <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500">No leads enrolled yet</p>
      </div>
    );
  }
  
  return (
    <div className="p-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Lead</TableHead>
            <TableHead>Current Step</TableHead>
            <TableHead>Progress</TableHead>
            <TableHead>Enrolled</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {enrollments.map((enrollment) => (
            <TableRow key={enrollment.id}>
              <TableCell>{getStatusIcon(enrollment.status)}</TableCell>
              <TableCell>
                <div>
                  <div className="font-medium">{enrollment.lead_name}</div>
                  <div className="text-xs text-slate-500">{enrollment.lead_email}</div>
                </div>
              </TableCell>
              <TableCell>{enrollment.current_step_name || '-'}</TableCell>
              <TableCell>
                <div className="text-sm">
                  {enrollment.steps_completed}/{enrollment.total_steps}
                </div>
              </TableCell>
              <TableCell className="text-sm text-slate-500">
                {new Date(enrollment.enrolled_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {enrollment.status === 'active' && (
                      <DropdownMenuItem onClick={() => handlePauseEnrollment(enrollment.id)}>
                        <Pause className="h-4 w-4 mr-2" /> Pause
                      </DropdownMenuItem>
                    )}
                    {enrollment.status === 'paused' && (
                      <DropdownMenuItem onClick={() => handleResumeEnrollment(enrollment.id)}>
                        <Play className="h-4 w-4 mr-2" /> Resume
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem className="text-red-600" onClick={() => handleUnenroll(enrollment.id)}>
                      <XCircle className="h-4 w-4 mr-2" /> Unenroll
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Analytics Tab Component
function AnalyticsTab({ sequence }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!sequence?.id) return;
    
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/sequences/${sequence.id}/analytics`);
        setAnalytics(response.data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [sequence?.id]);
  
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!analytics) {
    return (
      <div className="text-center py-8">
        <BarChart3 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500">No analytics data yet</p>
      </div>
    );
  }
  
  return (
    <div className="p-4 space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{analytics.summary.total_enrolled}</div>
            <div className="text-sm text-slate-500">Total Enrolled</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-green-600">{analytics.summary.completed}</div>
            <div className="text-sm text-slate-500">Completed</div>
            <div className="text-xs text-slate-400">{analytics.summary.completion_rate}% rate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-purple-600">{analytics.summary.converted}</div>
            <div className="text-sm text-slate-500">Converted</div>
            <div className="text-xs text-slate-400">{analytics.summary.conversion_rate}% rate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-orange-600">{analytics.summary.active}</div>
            <div className="text-sm text-slate-500">Active</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Email Stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-purple-500" />
            Email Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="text-2xl font-semibold">{analytics.email_stats.sent}</div>
              <div className="text-sm text-slate-500">Emails Sent</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-semibold text-green-600">{analytics.email_stats.opened}</div>
              <div className="text-sm text-slate-500">Opened</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-semibold text-blue-600">{analytics.email_stats.open_rate}%</div>
              <div className="text-sm text-slate-500">Open Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Step Performance */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            Step Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Step</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Executions</TableHead>
                <TableHead className="text-right">Successes</TableHead>
                <TableHead className="text-right">Success Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics.step_performance.map((step) => (
                <TableRow key={step.step_id}>
                  <TableCell className="font-medium">{step.step_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{step.step_type}</Badge>
                  </TableCell>
                  <TableCell className="text-right">{step.executions}</TableCell>
                  <TableCell className="text-right">{step.successes}</TableCell>
                  <TableCell className="text-right">
                    <span className={step.success_rate >= 80 ? 'text-green-600' : step.success_rate >= 50 ? 'text-yellow-600' : 'text-red-600'}>
                      {step.success_rate.toFixed(1)}%
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="h-5 w-5 text-slate-500" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {analytics.recent_activity.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <div>
                  <span className="font-medium">{activity.lead_name}</span>
                  <span className="text-sm text-slate-500 ml-2">→ {activity.current_step_name || 'Completed'}</span>
                </div>
                <Badge variant={activity.status === 'active' ? 'default' : activity.status === 'completed' ? 'secondary' : 'outline'}>
                  {activity.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
